import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DateAdapter,
  RdsDatepickerComponent,
  RdsDatepickerInputDirective,
  RdsDatepickerToggleComponent,
  RdsFormFieldComponent,
  RdsInputDirective,
  RdsProgressSpinnerComponent,
  RdsTextButtonComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import {
  CalendarDayViewComponent,
  CalendarEventTimesChangedEvent,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarWeekViewComponent,
} from 'angular-calendar';
import { endOfWeek } from 'date-fns/endOfWeek';
import { format } from 'date-fns/format';
import { startOfWeek } from 'date-fns/startOfWeek';

import {
  BookingRequestDetailsViewModel,
  CalendarData,
  CalendarEventTypeEnum,
  CustomDateAdapter,
  ViewportService,
  dateClass,
  utcDateToTimezoneDisplayDate,
} from '@bookly/shared';

import { RoomBookingCalendarService } from './room-booking-calendar.service';

@Component({
  selector: 'bsh-room-bookings-calendar',
  imports: [
    FormsModule,
    NgClass,
    RdsProgressSpinnerComponent,
    RdsFormFieldComponent,
    RdsInputDirective,
    RdsDatepickerInputDirective,
    RdsDatepickerToggleComponent,
    RdsDatepickerComponent,
    RdsTextButtonComponent,
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarDayViewComponent,
    CalendarWeekViewComponent,
    RdsTooltipDirective,
  ],
  providers: [
    RoomBookingCalendarService,
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './room-bookings-calendar.component.html',
  styleUrl: './room-bookings-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomBookingsCalendarComponent {
  readonly #roomsCalendarService = inject(RoomBookingCalendarService);
  protected readonly isMobileScreen = inject(ViewportService).isMobile;
  protected readonly roomEvents = this.#roomsCalendarService.roomAvailability;
  protected readonly isLoading =
    this.#roomsCalendarService.isCalendarDataLoading;
  protected bookingRequest: BookingRequestDetailsViewModel | null = null;
  protected viewDate: Date = new Date();
  protected readonly CalendarEventTypeEnum = CalendarEventTypeEnum;
  protected readonly dateClass = dateClass;
  #roomCalendarId: string | null = null;
  #semiPrivate: boolean = false;
  #siteTimeZone: string | null = null;

  @Input() set roomBooking(value: BookingRequestDetailsViewModel | null) {
    if (value) {
      this.#siteTimeZone = value.roomDetails.siteTimeZone ?? null;
      this.viewDate = utcDateToTimezoneDisplayDate(
        value.bookingDetails.startTime,
        this.#siteTimeZone
      );
      this.bookingRequest = value;
      this.#roomCalendarId = value.roomDetails.calendarId;
      this.#semiPrivate = value.roomDetails.semiPrivate;
      this.viewDateChange(this.viewDate);
    }
  }

  @Input() set calendarData(value: CalendarData) {
    if (value && value.roomCalendarId) {
      this.#roomCalendarId = value.roomCalendarId;
      this.#semiPrivate = value.semiPrivate;
      this.#siteTimeZone = value.siteTimeZone ?? this.#siteTimeZone;
      if (value.startDate) {
        this.viewDate = utcDateToTimezoneDisplayDate(
          new Date(value.startDate),
          this.#siteTimeZone
        );
      }
      this.viewDateChange(this.viewDate);
    }
  }

  @Input() stickyHeader: boolean = false;

  @Input() selectable: boolean = false;

  @Output() dateSelected = new EventEmitter<Date>();

  protected viewDateChange(calendarView: Date) {
    const startDate = startOfWeek(calendarView, {
      weekStartsOn: 1,
    });
    const endDate = endOfWeek(calendarView, { weekStartsOn: 1 });
    if (this.#roomCalendarId) {
      this.#roomsCalendarService
        .getRoomAvailability(
          this.#roomCalendarId,
          format(startDate, "yyyy-MM-dd'T'00:00:00"),
          format(endDate, "yyyy-MM-dd'T'23:59:59"),
          this.#semiPrivate,
          this.#siteTimeZone
        )
        .subscribe(() => {
          const id = this.bookingRequest?.bookingDetails.id;
          if (id) {
            this.#roomsCalendarService.setDraggable(id);
          }
        });
    }
  }

  protected hourClicked(event: { date: Date; sourceEvent: MouseEvent }) {
    this.dateSelected.emit(event.date);
  }

  protected eventTimeChanged(event: CalendarEventTimesChangedEvent) {
    this.dateSelected.emit(event.newStart);
  }
}
